import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPIWithoutAuth } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const fetchFooterData = createAsyncThunk(
    'footer/fetchFooterData',
    async (_, { rejectWithValue }) => {
        try {
            const response = await callAPIWithoutAuth(
                apiUrls.footerSetting,
                {},
                "GET",
                {}
            );

            if (response?.data?.success) {
                return response.data.data;
            } else {
                return rejectWithValue(response?.data?.message || 'Failed to fetch footer data');
            }
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Network error');
        }
    }
);

const footerSlice = createSlice({
    name: 'footer',
    initialState: {
        header: "",
        companyInfo: {
            logo: '',
            description: '',
        },

        socialLinks: [
            { platform: 'instagram', url: '#', icon: '/image/instagram.svg', active: true },
            { platform: 'facebook', url: '#', icon: '/image/facebook-f.svg', active: true },
            { platform: 'twitter', url: '#', icon: '/image/twitter.svg', active: true },
            { platform: 'linkedin', url: '#', icon: '/image/linkedin.svg', active: true },
        ],

        contactInfo: {
            support_info: '',
            phone: '',
            email: '',
            address: '',
            phoneIcon: '/image/f-calling.svg',
            emailIcon: '/image/f-message.svg',
        },

        paymentMethods: [
            { name: 'PayPal', icon: '/image/paypal-icon.svg', active: true },
        ],
        copyright: '',
        number_title: '',
        poweredByLink: '',
        poweredByText: '',
        loadingFooter: false,
        error: null,
        lastUpdated: null
    },
    reducers: {
        updateCompanyInfo: (state, action) => {
            state.companyInfo = { ...state.companyInfo, ...action.payload };
        },

        updateSocialLinks: (state, action) => {
            state.socialLinks = action.payload;
        },

        updateSocialLink: (state, action) => {
            const { index, data } = action.payload;
            if (state.socialLinks[index]) {
                state.socialLinks[index] = { ...state.socialLinks[index], ...data };
            }
        },

        addSocialLink: (state, action) => {
            state.socialLinks.push(action.payload);
        },

        removeSocialLink: (state, action) => {
            state.socialLinks = state.socialLinks.filter((_, index) => index !== action.payload);
        },

        updateContactInfo: (state, action) => {
            state.contactInfo = { ...state.contactInfo, ...action.payload };
        },

        updatePaymentMethods: (state, action) => {
            state.paymentMethods = action.payload;
        },

        updateCopyright: (state, action) => {
            state.copyright = action.payload;
        },

        removeHeader: (state) => {
            state.header = "";
        },

        resetFooterData: () => {
            return {
                header: "",
                companyInfo: {
                    logo: '',
                    description: '',
                },
                socialLinks: [
                    { platform: 'instagram', url: '#', icon: '/image/instagram.svg', active: true },
                    { platform: 'facebook', url: '#', icon: '/image/facebook-f.svg', active: true },
                    { platform: 'twitter', url: '#', icon: '/image/twitter.svg', active: true },
                    { platform: 'linkedin', url: '#', icon: '/image/linkedin.svg', active: true },
                ],
                contactInfo: {
                    support_info: '',
                    phone: '',
                    email: '',
                    phoneIcon: '/image/f-calling.svg',
                    emailIcon: '/image/f-message.svg',
                    facebook_name: '',
                    opening_hours: ''
                },
                paymentMethods: [
                    { name: 'PayPal', icon: '/image/paypal-icon.svg', active: true },
                ],
                number_title: '',
                copyright: '',
                poweredByLink: '',
                poweredByText: '',
                loadingFooter: false,
                error: null,
                lastUpdated: null
            };
        },

        clearFooterError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFooterData.pending, (state) => {
                state.loadingFooter = true;
                state.error = null;
            })
            .addCase(fetchFooterData.fulfilled, (state, action) => {
                state.loadingFooter = false;
                state.lastUpdated = new Date().toISOString();

                const footerData = Array.isArray(action.payload) && action.payload.length > 0
                    ? action.payload[0]
                    : null;

                if (footerData) {
                    state.header = footerData.header_notification || state.header;
                    state.companyInfo = {
                        ...state.companyInfo,
                        description: footerData.description || state.companyInfo.description,
                        logo: footerData.logo || state.companyInfo.logo,
                        header_logo_url: footerData.header_logo_url || state.header_logo_url,
                        footer_logo_url: footerData.footer_logo_url || state.footer_logo_url
                    };

                    state.contactInfo = {
                        ...state.contactInfo,
                        support_info: footerData.support_info || state.contactInfo.support_info,
                        phone: footerData.phone || state.contactInfo.phone,
                        email: footerData.email || state.contactInfo.email,
                        address: footerData.address || state.contactInfo.address,
                        facebook_name: footerData.facebook_name || state.contactInfo.facebook_name,
                        opening_hours: footerData.opening_hours || state.companyInfo.opening_hours,
                    };
                    state.number_title = footerData.number_title || state.number_title
                    state.copyright = footerData.footer_copyright || state.copyright
                    state.poweredByLink = footerData.powered_by_link || state.poweredByLink
                    state.poweredByText = footerData.powered_by_text || state.poweredByText
                    const newSocialLinks = [{
                        platform: 'facebook',
                        url: footerData.facebook ? footerData.facebook : '#',
                        icon: '/image/facebook-f.svg',
                        active: true
                    },
                    {
                        platform: 'instagram',
                        url: footerData.instagram ? footerData.instagram : '#',
                        icon: '/image/instagram.svg',
                        active: true
                    },
                    {
                        platform: 'twitter',
                        url: footerData.twitter ? footerData.twitter : '#',
                        icon: '/image/twitter.svg',
                        active: true
                    },
                    {
                        platform: 'linkedin',
                        url: footerData.linkedin ? footerData.linkedin : '#',
                        icon: '/image/linkedin.svg',
                        active: true
                    }
                    ];

                    if (newSocialLinks.length > 0) {
                        state.socialLinks = newSocialLinks;
                    }

                }
            })
            .addCase(fetchFooterData.rejected, (state, action) => {
                state.loadingFooter = false;
                state.error = action.payload;
            });
    }
});

export const selectCompanyInfo = (state) => state.footer.companyInfo;
export const selectSocialLinks = (state) => state.footer.socialLinks;
export const selectContactInfo = (state) => state.footer.contactInfo;
export const selectPaymentMethods = (state) => state.footer.paymentMethods;
export const selectCopyright = (state) => state.footer.copyright;
export const selectNumberTitle = (state) => state.footer.number_title;
export const selectPoweredByLink = (state) => state.footer.poweredByLink;
export const selectPoweredByText = (state) => state.footer.poweredByText;
export const selectFooterLoading = (state) => state.footer.loadingFooter;
export const selectFooterError = (state) => state.footer.error;
export const headerNotification = (state) => state.footer.header;


export const {
    updateCompanyInfo,
    updateSocialLinks,
    updateSocialLink,
    addSocialLink,
    removeSocialLink,
    updateContactInfo,
    updatePaymentMethods,
    updateCopyright,
    resetFooterData,
    clearFooterError,
    removeHeader
} = footerSlice.actions;

export default footerSlice.reducer;